import { Form, Formik, FormikProps } from 'formik';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTypeInput, TraitInput } from '../commons/input';
import { ColumnMapSelect, ColumnItem } from '../../../../types';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { EStatus, TFileData } from '@/models/fileUpload';
import { TRAITS } from '../../../../constants';
import { useColumnMapInitialValues } from '../hooks/useColumnMapInitialValues';
import { generateSlug } from '@/utils';
import { StepPageActions } from '@/global/components';
import { useWorkspace } from '@/features/workspaces/hooks';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { DataSource, DataSourceFileConfig, DataSourceIDEnum, Header } from '@/models/dataSource';
import { useGetTraitsQuery } from '@/services/endpoints/traits';
import { Select } from '@/lib/Selects';
import { Loadable } from '@/components/loaders';
import { useSaveDataSourceMutation } from '@/services/dataSources';

type FileDataSourceColumnMap = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
  isToCheckUploadSuccess?: boolean;
};

// 3 rows + header
const MAX_ROWS = 4;

export const FileDataSourceColumnMapStep: FC<FileDataSourceColumnMap> = ({ onNextStep, onPrevStep, isToCheckUploadSuccess = false }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);
  const { t } = useTranslation('data_source_edit');

  const [saveDataSource] = useSaveDataSourceMutation();

  const columnMapFormRef = useRef<FormikProps<ColumnItem[]>>(null);

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const fileData: TFileData | undefined = useAppSelector((state) => state.fileUploading.fileData);

  const { data: groupedTraits, isLoading: isLoadingTraits } = useGetTraitsQuery({ workspaceId: workspace.id });

  const columnMapSelect: ColumnMapSelect[] = [
    {
      id: TRAITS.MAP_TO_TRAIT,
      label: t('file.map_column.map_to_trait'),
    },
    {
      id: TRAITS.CUSTOM_TRAIT,
      label: t('file.map_column.custom_trait'),
    },
    {
      id: TRAITS.CONSENT_TRAIT,
      label: t('file.map_column.consent_trait'),
    },
    {
      id: TRAITS.IGNORE_COLUMN,
      label: t('file.map_column.ignore_column'),
    },
  ];

  const fileHeader: string[] = fileData ? [...fileData.header] : [];
  const fileContent: string[][] = fileData
    ? fileData.isDefaultHeader
      ? [...fileData.preview.slice(0, MAX_ROWS).slice(1)]
      : [...fileData.preview.slice(0, MAX_ROWS)]
    : [];

  const validateForm = (values: ColumnItem[]) => {
    const errors: { [key: number]: string }[] = [];
    values.filter((column, index) => {
      switch (column.trait.id) {
        case TRAITS.MAP_TO_TRAIT:
          if (!column.traitValue) {
            errors[index] = t('file.map_column.validation.select_trait');
          }
          break;
        case TRAITS.CUSTOM_TRAIT:
          if (!column.traitValue || !column.traitValue.id || !column.traitValue.displayValue || !column.customDataType) {
            errors[index] = t('file.map_column.validation.value_cannot_be_blank');
          }
          break;
        case TRAITS.CONSENT_TRAIT:
          // in the future we need more validations
          if (!column.traitValue) {
            errors[index] = t('file.map_column.validation.value_cannot_be_blank');
          }
          break;
        default:
          break;
      }
    });
    return errors;
  };

  const initialValues = useColumnMapInitialValues({ fileHeader, columnMapSelect, groupedTraits, defaultNonMatchingTraitId: 3 });

  const getHeadersFromTraitValue = (columnMap: ColumnItem[]): Header[] => {
    return columnMap.map((column) => {
      if (column.trait.id !== TRAITS.IGNORE_COLUMN) {
        return {
          name: column.header,
          traitId: column.traitValue?.id || '',
          displayName: column.traitValue?.displayValue || '',
          custom: column.trait.id === TRAITS.CUSTOM_TRAIT,
          customDataType: column.customDataType,
          skip: false,
        };
      }
      return {
        name: column.header,
        skip: column.trait.id === TRAITS.IGNORE_COLUMN,
      };
    });
  };

  const handleNextStep = async () => {
    await columnMapFormRef.current?.submitForm();

    if (columnMapFormRef.current?.isValid && columnMapFormRef.current?.values) {
      const columnMap: ColumnItem[] = columnMapFormRef.current?.values;

      const headers: Header[] = getHeadersFromTraitValue(columnMap);

      if (dataSource && dataSource.name && dataSource.configuration) {
        dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers } }));
        const configuration = dataSource.configuration || ({} as any);

        let finalConfiguration = { ...configuration };

        // Only apply rules when needed
        if (dataSource.type.id === DataSourceIDEnum.SFTP) {
          // omit authMethod only
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { authMethod, ...rest } = finalConfiguration;
          finalConfiguration = rest;
        }

        if (dataSource.type.id === DataSourceIDEnum.CsvFile || dataSource.type.id === DataSourceIDEnum.EncryptedCsvFile) {
          // omit filename only
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { filename, ...rest } = finalConfiguration;
          finalConfiguration = rest;
        }

        await saveDataSource({
          workspaceId: workspace.id,
          dataSource: {
            type: dataSource.type.id,
            name: dataSource.name,
            configuration: {
              ...finalConfiguration,
              type: dataSource.configuration.type,
              isDefaultHeader: dataSource.configuration.isDefaultHeader,
              headers,
              url: dataSource.configuration.url || fileUploadStatus?.data?.path,
            },
          },
        })
          .then((_res: any) => {
            if (!_res?.error) {
              onNextStep();
            }
          })
          .catch();
      }
    }
  };

  const handlePrevStep = async () => {
    await columnMapFormRef.current?.submitForm();

    if (columnMapFormRef.current?.isValid && columnMapFormRef.current?.values) {
      const columnMap: ColumnItem[] = columnMapFormRef.current?.values;
      const headers: Header[] = getHeadersFromTraitValue(columnMap);

      if (dataSource && dataSource.name && dataSource.configuration) {
        dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers: headers } }));
        onPrevStep();
      }
    }
  };

  const isUploadSuccess = fileUploadStatus?.uploadingStatus === EStatus.SUCCESS;

  return (
    <Loadable isLoading={isLoadingTraits}>
      <Formik
        onSubmit={(values, { validateForm }) => validateForm(values)}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
        validate={validateForm}
        innerRef={columnMapFormRef}
      >
        {({ values, setFieldValue, setFieldError, errors }) => (
          <Form className='flex flex-col gap-8'>
            {values &&
              fileContent &&
              values.map((TraitValue, index) => {
                return (
                  <div className='flex gap-8' key={TraitValue.id}>
                    <div className='w-72 min-w-72 overflow-hidden rounded-xl border'>
                      {/* Header */}
                      <div className=' bg-secondary-background px-6 py-3'>
                        <p className='text-sm font-semibold text-quaternary-500'>{TraitValue.header}</p>
                      </div>
                      {/* Content */}
                      {fileContent.map((fileRows, i) => (
                        <div key={`${fileRows[index]}_${i}`} className='border-t p-6 text-sm text-tertiary-600'>
                          {fileRows[index]}
                        </div>
                      ))}
                    </div>
                    <Select
                      items={columnMapSelect}
                      value={TraitValue.trait}
                      onChange={(selectItemValue) => {
                        setFieldError(String(index), undefined);
                        if (selectItemValue.id == TRAITS.CUSTOM_TRAIT) {
                          setFieldValue(String(index), {
                            ...TraitValue,
                            trait: selectItemValue,
                            traitValue: {
                              id: generateSlug(TraitValue.header),
                              displayValue: TraitValue.header,
                            },
                          });
                        } else {
                          setFieldValue(String(index), { ...TraitValue, trait: selectItemValue, traitValue: undefined });
                        }
                      }}
                    />
                    <div className='min-w-48 max-w-64'>
                      <TraitInput
                        option={TraitValue.trait}
                        error={errors[index] as string}
                        value={TraitValue.traitValue}
                        onChange={(value) => {
                          setFieldError(String(index), undefined);
                          setFieldValue(String(index), { ...TraitValue, traitValue: value });
                        }}
                      />
                    </div>
                    <DataTypeInput
                      option={TraitValue.trait}
                      error={errors[index] as string}
                      value={TraitValue.customDataType}
                      onChange={(value) => {
                        setFieldError(String(index), undefined);
                        setFieldValue(String(index), { ...TraitValue, customDataType: value });
                      }}
                    />
                  </div>
                );
              })}
          </Form>
        )}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON:confirm',
          onClick: handleNextStep,
          disabled: isToCheckUploadSuccess && !isUploadSuccess,
        }}
        secondaryButton={{
          text: 'COMMON:cancel',
          onClick: handlePrevStep,
        }}
        isPrimaryButtonTooltipEnabled={isToCheckUploadSuccess && !isUploadSuccess}
        tooltipContent={t('file_upload_issue')}
      />
    </Loadable>
  );
};
